import { Calendar } from '@/client/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/client/components/ui/popover';
import {
  formatLongDate,
  formatRelativeDay,
  formatShortDate,
  parseDateInput,
  parseISODate,
  startOfToday,
  toISODate,
} from '@/client/lib/date-input';
import { cn } from '@/client/lib/utils';
import { CalendarIcon, CornerDownLeft, Loader2 } from 'lucide-react';
import * as React from 'react';
import type { Matcher } from 'react-day-picker';

export type DatePreset = { label: string; value: string };

type DatePickerProps = {
  /** Selected date as `YYYY-MM-DD`, or empty string for none. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest selectable date, `YYYY-MM-DD`. */
  min?: string;
  /** Latest selectable date, `YYYY-MM-DD`. */
  max?: string;
  /** Blocks native form submission while empty, like `<input required>`. Also hides "Clear". */
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** `default` matches `<Input>`; `sm` is the compact h-8 / text-xs size. */
  size?: 'default' | 'sm';
  /** `ghost` is borderless until hovered, for inline table cells. */
  variant?: 'default' | 'ghost';
  /** Hide the calendar icon (useful in narrow cells). */
  hideIcon?: boolean;
  /** Show a muted "In 3 days" style hint in the field. Defaults to on for the default size. */
  showRelative?: boolean;
  /** Quick-pick chips shown under the calendar, e.g. payment terms. */
  presets?: DatePreset[];
  /** Custom display formatter for the selected `YYYY-MM-DD` value. */
  formatValue?: (iso: string) => string;
  className?: string;
  id?: string;
  'aria-label'?: string;
};

function clampDate(date: Date, min?: Date, max?: Date): Date {
  if (min && date < min) return min;
  if (max && date > max) return max;
  return date;
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  required,
  placeholder = 'Pick a date',
  disabled,
  loading,
  size = 'default',
  variant = 'default',
  hideIcon,
  showRelative,
  presets,
  formatValue = formatShortDate,
  className,
  id,
  'aria-label': ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const selected = parseISODate(value);
  const minDate = parseISODate(min);
  const maxDate = parseISODate(max);
  const today = startOfToday();
  const [month, setMonth] = React.useState<Date>(() =>
    monthStart(selected ?? clampDate(today, minDate, maxDate)),
  );

  const isAllowed = (date: Date) => !(minDate && date < minDate) && !(maxDate && date > maxDate);

  // Text box: live parse + preview
  const parsed = text.trim() ? parseDateInput(text) : null;
  const parsedAllowed = parsed ? isAllowed(parsed) : false;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      setText('');
      setMonth(monthStart(selected ?? clampDate(today, minDate, maxDate)));
    }
  }

  function commit(date: Date) {
    onChange(toISODate(date));
    setOpen(false);
  }

  function handleTextChange(next: string) {
    setText(next);
    const date = next.trim() ? parseDateInput(next) : null;
    if (date) setMonth(monthStart(date));
  }

  const disabledDays: Matcher[] = [];
  if (minDate) disabledDays.push({ before: minDate });
  if (maxDate) disabledDays.push({ after: maxDate });

  // Dropdown range: ±10 years around today, widened to include min/max/selected.
  const rangeStart = monthStart(
    new Date(
      Math.min(
        new Date(today.getFullYear() - 10, 0, 1).getTime(),
        selected?.getTime() ?? Number.POSITIVE_INFINITY,
      ),
    ),
  );
  const rangeEnd = new Date(
    Math.max(new Date(today.getFullYear() + 10, 11, 31).getTime(), selected?.getTime() ?? 0),
  );

  const relative = showRelative ?? (size === 'default' && variant === 'default');
  const todayAllowed = isAllowed(today);
  const canClear = !required && !!value;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className={cn('relative w-full', className)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            aria-label={ariaLabel}
            disabled={disabled || loading}
            className={cn(
              'group/date flex w-full items-center text-left transition-all duration-150 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              variant === 'default' && [
                'rounded-md border border-input bg-background shadow-sm',
                'hover:border-foreground/20 hover:bg-accent/40',
                'focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring',
                'data-[state=open]:border-ring data-[state=open]:ring-1 data-[state=open]:ring-ring',
              ],
              variant === 'ghost' &&
                'rounded border border-transparent bg-transparent hover:border-border focus-visible:border-border data-[state=open]:border-ring',
              size === 'default' && 'h-9 gap-2 px-3 py-1 text-sm',
              size === 'sm' && 'h-8 gap-1.5 px-2.5 text-xs',
              variant === 'ghost' && size === 'sm' && 'h-auto px-1 py-0.5',
              'tabular-nums',
            )}
          >
            {!hideIcon && (
              <CalendarIcon
                className={cn(
                  'shrink-0 text-muted-foreground transition-colors group-hover/date:text-foreground group-data-[state=open]/date:text-foreground',
                  size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4',
                )}
              />
            )}
            {selected ? (
              <>
                <span className="shrink-0 truncate">{formatValue(value)}</span>
                {relative && (
                  <span className="ml-auto min-w-0 truncate pl-1 text-xs text-muted-foreground">
                    {formatRelativeDay(selected)}
                  </span>
                )}
              </>
            ) : (
              <span className="flex-1 truncate text-muted-foreground">{placeholder}</span>
            )}
            {loading && (
              <Loader2 className="ml-auto h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
            )}
          </button>
        </PopoverTrigger>
        {required && (
          // Invisible proxy so native form validation still blocks empty submissions.
          <input
            tabIndex={-1}
            aria-hidden
            required
            value={value}
            onChange={() => {}}
            onFocus={() => handleOpenChange(true)}
            className="pointer-events-none absolute inset-x-0 bottom-0 h-px w-full opacity-0"
          />
        )}
      </div>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto overflow-hidden rounded-xl border-border/80 p-0 shadow-xl shadow-black/5"
      >
        {/* Type-a-date */}
        <div className="border-b bg-muted/30 px-3 py-2.5">
          <div className="relative">
            <input
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (parsed && parsedAllowed) commit(parsed);
                }
              }}
              placeholder="Type a date…"
              aria-label="Type a date"
              className="h-8 w-full rounded-md border border-input bg-background px-2.5 pr-8 text-sm shadow-sm placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <kbd
              className={cn(
                'pointer-events-none absolute right-1.5 top-1/2 inline-flex h-5 -translate-y-1/2 items-center rounded border bg-muted px-1 text-muted-foreground transition-opacity',
                parsed && parsedAllowed ? 'opacity-100' : 'opacity-0',
              )}
            >
              <CornerDownLeft className="h-3 w-3" />
            </kbd>
          </div>
          <p
            className={cn(
              'mt-1.5 h-4 truncate text-xs',
              !text.trim() && 'text-muted-foreground/70',
              text.trim() && parsed && parsedAllowed && 'text-foreground',
              text.trim() && (!parsed || !parsedAllowed) && 'text-destructive',
            )}
          >
            {!text.trim()
              ? 'e.g. 15/10, 15 oct, fri, tomorrow, +30'
              : !parsed
                ? 'Couldn’t read that date'
                : !parsedAllowed
                  ? `${formatLongDate(parsed)} is out of range`
                  : formatLongDate(parsed)}
          </p>
        </div>

        <Calendar
          mode="single"
          animate
          captionLayout="dropdown"
          startMonth={rangeStart}
          endMonth={rangeEnd}
          month={month}
          onMonthChange={setMonth}
          selected={parsed && parsedAllowed ? parsed : selected}
          disabled={disabledDays}
          weekStartsOn={1}
          onSelect={(date) => {
            if (date) commit(date);
          }}
        />

        {presets && presets.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pb-3">
            {presets.map((p) => {
              const date = parseISODate(p.value);
              const active = p.value === value;
              const allowed = date ? isAllowed(date) : false;
              return (
                <button
                  key={p.label}
                  type="button"
                  disabled={!allowed}
                  onClick={() => date && commit(date)}
                  title={date ? formatLongDate(date) : undefined}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40',
                    active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t bg-muted/30 px-3 py-2">
          <button
            type="button"
            disabled={!todayAllowed}
            onClick={() => commit(today)}
            className="rounded-md px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
          >
            Today
          </button>
          {canClear ? (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Clear
            </button>
          ) : (
            <span className="truncate text-xs text-muted-foreground">
              {selected ? formatLongDate(selected) : ''}
            </span>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
